import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGame } from '@/context/GameContext';
import Logo from './Logo';
import { loginWithDiscord, loginWithGitHub, loginWithGoogle } from '@/lib/auth/session';

// XP needed per level (simple 500xp per level curve)
const XP_PER_LEVEL = 500;

export const Navbar: React.FC = () => {
  const { user, isMockMode, signInMock } = useAuth();
  const { stats } = useGame();

  const handleLogin = (provider: 'github' | 'google' | 'discord') => {
    if (isMockMode) { signInMock(); return; }
    if (provider === 'github') loginWithGitHub();
    if (provider === 'google') loginWithGoogle();
    if (provider === 'discord') loginWithDiscord();
  };

  const initials = (stats?.handle ?? 'SZ').slice(0, 2).toUpperCase();

  const xpInLevel = (stats?.xp ?? 0) % XP_PER_LEVEL;
  const xpBarPct = Math.min(100, Math.round((xpInLevel / XP_PER_LEVEL) * 100));
  const livesCount = stats?.lives ?? 0;

  return (
    <nav className="fixed top-0 left-0 right-0 z-[100] flex h-16 w-full items-center justify-between bg-[#0e0e0e] px-6 shadow-[0_0_20px_rgba(140,172,255,0.08)] border-b border-white/[0.04]">
      {/* Logo */}
      <Logo size="md" linkTo="/" />

      {user?.id ? (
        /* ── Logged-in: game HUD ── */
        <div className="flex items-center gap-2.5">

          {/* Lives */}
          <div className="flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-950/40 px-3 py-1">
            {Array.from({ length: Math.min(livesCount, 3) }).map((_, i) => (
              <span key={i} className="text-[11px] leading-none text-red-400">♥</span>
            ))}
            {livesCount > 3 && (
              <span className="font-['Space_Grotesk'] text-[11px] font-bold text-red-400">+{livesCount - 3}</span>
            )}
            <span className="ml-0.5 font-['Space_Grotesk'] text-[11px] font-bold text-red-400">{livesCount}</span>
          </div>

          {/* Streak */}
          <div className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-950/40 px-3 py-1">
            <span className="text-[13px] leading-none">🔥</span>
            <span className="font-['Space_Grotesk'] text-[11px] font-bold text-amber-400">
              {stats?.streak_days ?? 0}
            </span>
          </div>

          {/* Level */}
          <div className="flex items-center rounded-full border border-emerald-500/30 bg-emerald-950/40 px-3 py-1">
            <span className="font-['Space_Grotesk'] text-[11px] font-bold text-emerald-400">
              Lv {stats?.level ?? 1}
            </span>
          </div>

          {/* XP bar + label — hidden on small screens */}
          <div className="hidden items-center gap-2 md:flex">
            <div className="h-[3px] w-20 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-[#00cffc] transition-all duration-700"
                style={{ width: `${xpBarPct}%` }}
              />
            </div>
            <span className="font-['Space_Grotesk'] text-[11px] font-medium text-white/40">
              {(stats?.xp ?? 0).toLocaleString()} xp
            </span>
          </div>

          {/* Avatar with notification dot */}
          <Link
            to="/profile"
            aria-label="Profile"
            className="relative ml-1 flex h-9 w-9 items-center justify-center rounded-full border border-[#8cacff]/20 bg-[#1a1a1a] font-['Space_Grotesk'] text-xs font-black text-[#00cffc] transition-all hover:border-[#00cffc]/50 hover:shadow-[0_0_8px_rgba(0,207,252,0.25)]"
          >
            {initials}
            {/* Online / active dot */}
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0e0e0e]" />
          </Link>
        </div>
      ) : (
        /* ── Logged-out: two login CTAs ── */
        <div className="flex items-center gap-3">
          {/* GitHub — ghost/bordered */}
          <button
            type="button"
            onClick={() => handleLogin('github')}
            className="flex items-center gap-2 rounded-full border border-white/20 bg-transparent px-4 py-2 font-['Space_Grotesk'] text-xs font-bold uppercase tracking-wider text-white/80 transition-all hover:border-white/40 hover:bg-white/5 hover:text-white"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4 flex-shrink-0 fill-current" aria-hidden="true">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
            </svg>
            Login with GitHub
          </button>

          {/* Google — filled blue */}
          <button
            type="button"
            onClick={() => handleLogin('google')}
            className="flex items-center gap-2 rounded-full bg-[#4285f4] px-4 py-2 font-['Space_Grotesk'] text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-[#3367d6] hover:shadow-[0_0_12px_rgba(66,133,244,0.4)]"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0" aria-hidden="true">
              <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Login with Google
          </button>

          {/* Discord — ghost */}
          <button
            type="button"
            onClick={() => handleLogin('discord')}
            className="flex items-center gap-2 rounded-full border border-[#5865F2]/40 bg-transparent px-4 py-2 font-['Space_Grotesk'] text-xs font-bold uppercase tracking-wider text-white/80 transition-all hover:border-[#5865F2] hover:bg-[#5865F2]/10 hover:text-white"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0 fill-[#5865F2]" aria-hidden="true">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            Login with Discord
          </button>
        </div>
      )}
    </nav>
  );
};
